/* Common Imports */

import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	SetDefaultLocationThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	updateLocationPropertyThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	Paper,
	Snackbar,
	Tab,
	Tabs,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { Box, Stack, styled } from "@mui/system";
import { useLoadScript } from "@react-google-maps/api";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import usePlacesAutocomplete from "use-places-autocomplete";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import Search from "./SearchPropertyLocation";

/* Icon Imports */

import { AltRoute, Close, EditOutlined } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/*
&.Location Picker library imports

*/

const LocationPicker = dynamic<{
	containerElement: React.ReactNode;
	mapElement: React.ReactNode;
	onChange: Function;
	defaultPosition: object;
	zoom?: number;
	radius: number;
	circleOptions?: object;
	//@ts-ignore
}>(() => import("@beegru/react-location-picker"), {
	ssr: false,
});

/*

& Mapbox API Key

*/

mapboxgl.accessToken = process.env.MAPBOXGL_ACCESS_TOKEN || "";

/*

& Next, let's describe an interface to determine the shape of the body that we will send to the GecodeApi endpoint.

*/

interface GeolocationCoordinates {
	latitude: number;
	longitude: number;
	altitude: number | null;
	accuracy: number;
	altitudeAccuracy: number | null;
	heading: number | null;
	speed: number | null;
}
interface GeolocationPosition {
	coords: GeolocationCoordinates;
}
interface GMRGCPlusCode {
	compound_code: string;
	global_code: string;
}
interface GMRGCResultsAddressComponent {
	long_name: string;
	short_name: string;
	types: Array<string>;
}
interface GMRGCResultsGeometryLocation {
	lat: number;
	lng: number;
}
interface GMRGCResultsGeometryBoundsAndGeometryViewport {
	northeast: GMRGCResultsGeometryLocation;
	southwest: GMRGCResultsGeometryLocation;
}
interface GMRGCResultsGeometry {
	bounds: GMRGCResultsGeometryBoundsAndGeometryViewport;
	location: GMRGCResultsGeometryLocation;
	location_type: string;
}
interface GMRGCResults {
	address_components: Array<GMRGCResultsAddressComponent>;
	formatted_address: string;
	geometry: GMRGCResultsGeometry;
	viewport: GMRGCResultsGeometryBoundsAndGeometryViewport;
	place_id: string;
	types: Array<string>;
}
interface GoogleMapsReverseGeocodingApiResponse {
	plus_code: GMRGCPlusCode;
	results: Array<GMRGCResults>;
	status: string;
}

type MapContainerProps = {
	showMap: boolean;
};

/* Styled Components */

const MapboxContainer = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const MapContainerDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
		justifyContent: "space-between",
		alignItems: "flex-start",
	},
}));

const MapContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "showMap",
})<MapContainerProps>(({ theme, showMap }) => ({
	width: "100%",
	display: showMap ? undefined : "none",
	overflow: "hidden",
}));

const MapContainer2 = styled("div", {
	shouldForwardProp: (prop) => prop !== "showMap",
})<MapContainerProps>(({ theme, showMap }) => ({
	width: "100%",
	display: showMap ? undefined : "none",
	overflow: "hidden",
}));

const Map = styled("div")(({ theme }) => ({
	height: "15rem",
	overflow: "hidden",
	borderRadius: "16px",
	[theme.breakpoints.up("md1190")]: {
		height: "20rem",
	},
}));

const Map2 = styled("div")(({ theme }) => ({
	height: "15rem",
	overflow: "hidden",
	borderRadius: "16px",
	[theme.breakpoints.up("md1190")]: {
		height: "20rem",
	},
}));

const EditContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.5rem",
	padding: "0rem 0rem 0.25rem 0rem",
	width: "100%",
}));

const SearchContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.688rem",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

/* Info box custom styles */

const InfoContainer = styled("div")(({ theme }) => ({
	zIndex: 1,
	top: "0.5rem",
	left: "0.5rem",
	maxWidth: "60%",
	minWidth: "200px",
	borderRadius: "16px",
	position: "absolute",
	padding: "0.75rem",
	background: theme.palette.background.paper,
}));

const DirectionContainer = styled("div")(({ theme }) => ({
	[theme.breakpoints.down(532)]: {
		display: "none",
	},
}));

const DetailContainer = styled(Stack)(({ theme }) => ({
	gap: "10px",
	maxWidth: "200px",
	[theme.breakpoints.down(532)]: {
		gap: "0",
	},
}));

const DetailTypography = styled(Typography)(({ theme }) => ({
	[theme.breakpoints.down(532)]: {
		display: "none",
	},
}));

const VicinityContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "1rem 0rem 0rem 0rem",
}));

const TabContextContainer = styled(TabContext)(({ theme }) => ({
	width: "100%",
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
}));

const librariesToLoad: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = [
	"places",
	"drawing",
	"geometry",
];

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const sortVicinityByDistance = (array: Array<property_vicinity_values>) => {
	let arr = [...array];

	return arr.sort((a: property_vicinity_values, b: property_vicinity_values) => (a.distance > b.distance ? 1 : -1));
};

interface property_vicinity_values {
	title: string;
	distance: number;
}

interface property_vicinity {
	category: string;
	values: Array<property_vicinity_values>;
}

/* Info Box */

const InfoBox = ({
	title,
	location,
	loading = false,
	localLang,
}: {
	loading: boolean;
	title: string;
	location: [number, number];
	localLang: any;
}) => {
	const [detail, setDetail] = React.useState<any>(null);

	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY || "",
		libraries: librariesToLoad as any,
	});

	React.useEffect(() => {
		if (isLoaded) {
			const geoCoder = new google.maps.Geocoder();
			const latlng = new google.maps.LatLng(location[0], location[1]);
			geoCoder.geocode({ location: latlng }, (res) => {
				if (res && res.length) {
					setDetail(res[0]);
				}
			});
		}
	}, [isLoaded, title, location, loading]);

	if (!title || !location || !detail || !location.length) return undefined;

	return (
		<InfoContainer>
			<Stack
				gap={"8px"}
				direction={"row"}
			>
				<DetailContainer>
					<Stack>
						<Typography
							variant="body2"
							fontWeight={500}
						>
							{title}
						</Typography>
						<DetailTypography variant="caption">{detail.formatted_address}</DetailTypography>
					</Stack>
					<Typography
						target="_blank"
						component={"a"}
						variant="caption"
						sx={{ color: "#1a73e8", textDecoration: "none" }}
						href={`https://www.google.com/maps/search/?api=1&query=${location[0]},${location[1]}`}
					>
						{localLang.view_larger_map}
					</Typography>
				</DetailContainer>
				<DirectionContainer>
					<Stack
						component={"a"}
						target="_blank"
						paddingX={"7px"}
						alignItems={"center"}
						sx={{ color: "#1a73e8", textDecoration: "none" }}
						href={`https://www.google.com/maps/dir/?api=1&destination=${title}&destination_place_id=${detail.place_id}`}
					>
						<AltRoute sx={{ width: 24, height: 24 }} />
						<Typography variant="caption">{localLang.directions}</Typography>
					</Stack>
				</DirectionContainer>
			</Stack>
		</InfoContainer>
	);
};

const Mapbox = ({
	dispatch,
	propertyData,
	property_created_by,
	property_id,
	session,
	location,
	isEditor,
	profile_context,
	section_to_edit,
	data_before_edit,
	loading,
	map_snackbar,
	trigger,
	updateTrigger,
	property_vicinity,
}: {
	dispatch: Function;
	propertyData: any;
	property_created_by: LocalPropertyInterface["created_by"];
	property_id: string | ObjectId;
	session: any;
	location: StateInterface["location"];
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	section_to_edit: string;
	data_before_edit: any;
	loading: boolean;
	map_snackbar: boolean;
	trigger: boolean;
	updateTrigger: (state: boolean) => void;
	property_vicinity: Array<property_vicinity>;
}) => {
	const theme = useTheme();
	const router = React.useRef(useRouter()).current;

	const [fromList, setFromList] = React.useState(null);
	const [marker, setMarker] = React.useState<any>(null);
	const [firstLoad, setFirstLoad] = React.useState(true);

	const [isEditing, setIsEditing] = React.useState(false);

	const [load, setLoad] = React.useState<boolean>(false);
	const [change, setChange] = React.useState<{ lat: number; lng: number }>({
		lat: location.location[0],
		lng: location.location[1],
	});

	const [lng, setLng] = React.useState(location.location[1]);
	const [lat, setLat] = React.useState(location.location[0]);

	const [updateLocationButtonText, setUpdateLocationButtonText] = React.useState<string>(
		router.locale === "ar-AE" ? "تحديث" : "Update",
	);
	const [updateLocationButtonDisabled, setUpdateLocationButtonDisabled] = React.useState<boolean>(false);

	const [emptyText, setEmptyText] = React.useState<boolean>(false);

	const sessionId = session ? (session.session_id ? session.session_id : "") : "";

	const [tabValue, setTabValue] = React.useState("schools");

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
	};

	React.useEffect(() => {
		if (section_to_edit !== "property-map") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	/*

	& Let's declare the router. Here we are using the useRouter hook from nextjs.

	& We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

	*/

	const localLang = {
		aboutLocation: router.locale === "kn-IN" ? "ಮ್ಯಾಪ್" : router.locale === "ar-AE" ? "الموقع" : "Location",
		snackValue:
			router.locale === "kn-IN"
				? "ಅಪ್ಡೇಟ್ ಯಶಸ್ವಿಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث الموقع بنجاح"
					: "Location updated successfully",
		enterManually:
			router.locale === "kn-IN"
				? "ಕ್ಷೇತ್ರಗಳು ಖಾಲಿ ಇರಲು ಸಾಧ್ಯವಿಲ್ಲ!"
				: router.locale === "ar-AE"
					? "أدخل الموقع يدويًا"
					: "Enter location manually",
		pinLocation:
			router.locale === "ar-AE"
				? "حدد موقعك على الخريطة"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ನಕ್ಷೆಯಲ್ಲಿ ಪಿನ್ ಮಾಡಿ"
					: "Pin your location on the map",
		enterLocation:
			router.locale === "ar-AE"
				? " أدخل موقع العقار"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ನಮೂದಿಸಿ "
					: "Enter property location ",
		schools: router.locale === "kn-IN" ? "ಶಾಲೆಗಳು" : router.locale === "ar-AE" ? "المدارس" : "Schools",
		hospitals: router.locale === "kn-IN" ? "ಆಸ್ಪತ್ರೆಗಳು" : router.locale === "ar-AE" ? "المستشفيات" : "Hospitals",
		malls: router.locale === "kn-IN" ? "ಮಾಲ್ಸ್" : router.locale === "ar-AE" ? "المراكز التجارية" : "Malls",
		others: router.locale === "kn-IN" ? "ಇತರೆ" : router.locale === "ar-AE" ? "الآخرون" : "Others",
		km: router.locale === "kn-IN" ? "ಕಿಮೀ " : router.locale === "ar-AE" ? "كم " : " km",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		view_larger_map:
			router.locale === "kn-IN"
				? "ದೊಡ್ಡ ನಕ್ಷೆಯಲ್ಲಿ ವೀಕ್ಷಿಸಿ"
				: router.locale === "ar-AE"
					? "عرض خريطة أكبر"
					: "View larger map",
		directions: router.locale === "kn-IN" ? "ಮಾರ್ಗದರ್ಶನ" : router.locale === "ar-AE" ? "الاتجاهات" : "Directions",
		please_enter_custom_title:
			router.locale === "kn-IN"
				? "ದಯವಿಟ್ಟು ನಿಮ್ಮ ಸ್ವಂತ ಶೀರ್ಷಿಕೆಯನ್ನು ನಮೂದಿಸಿ"
				: router.locale === "ar-AE"
					? "يرجى إدخال العنوان المخصص للموقع"
					: "Please enter the location's custom title",
		enter_custom_title:
			router.locale === "kn-IN"
				? "ನಿಮ್ಮ ಸ್ವಂತ ಶೀರ್ಷಿಕೆಯನ್ನು ನಮೂದಿಸಿ"
				: router.locale === "ar-AE"
					? "أدخل العنوان المخصص"
					: "Enter custom title",
		updating:
			router.locale === "kn-IN"
				? "ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ..."
				: router.locale === "ar-AE"
					? "جاري التحديث..."
					: "Updating...",
		please_enter_location:
			router.locale === "kn-IN"
				? "ದಯವಿಟ್ಟು ಸ್ಥಳವನ್ನು ನಮೂದಿಸಿ"
				: router.locale === "ar-AE"
					? "يرجى إدخال الموقع"
					: "Please enter location",
	};

	/* State to check if map is loaded */

	const map = React.useRef<any>(null);
	const [address, setAddress] = React.useState<string>("");
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY || "",
		libraries: librariesToLoad as any,
	});

	const [state, setState] = React.useState({
		address: "",
		position: {
			lat: location.location[0],
			lng: location.location[1],
		},
	});

	const {
		value,
		suggestions: { data },
		setValue,
	} = usePlacesAutocomplete({
		initOnMount: false,
		debounce: 500,
	});

	const [customTitle, setCustomTitle] = React.useState<string>("");
	const [locaionText, setLocationText] = React.useState<string>("");

	const [disabledText, setDisabledText] = React.useState<boolean>(true);

	const [helperText, setHelperText] = React.useState<string>("");

	const [isMapboxThingyLoaded, setIsMapboxThingyLoaded] = React.useState<boolean>(false);

	/*

	& initialize mapbox map

	*/

	React.useEffect(() => {
		if (isMapboxThingyLoaded) {
			return;
		}

		const isMobile = /Mobi|Android/i.test(navigator.userAgent);

		// Initialize the Mapbox map
		map.current = new mapboxgl.Map({
			container: map.current,
			style: "mapbox://styles/beegrurealty/ckxj2dccl09h714krfwuepaqt",
			center: [lng, lat],
			zoom: 15,
			cooperativeGestures: false,
		});

		map.current.scrollZoom.disable();
		if (isMobile) {
			map.current.touchZoomRotate.enable();
			map.current.touchZoomRotate.enableRotation();
		}

		// Add fullscreen control
		map.current.addControl(new mapboxgl.FullscreenControl());

		setIsMapboxThingyLoaded(true);
	}, [isMapboxThingyLoaded, lat, lng]);

	const enableScrollZoom = () => {
		if (map.current) {
			map.current.scrollZoom.enable();
		}
	};

	const disableScrollZoom = () => {
		if (map.current) {
			map.current.scrollZoom.disable();
		}
	};

	React.useEffect(() => {
		if (!isMapboxThingyLoaded) {
			return;
		}

		setChange({
			lat: location.location[0],
			lng: location.location[1],
		});
		setState({
			address: "",
			position: {
				lat: location.location[0],
				lng: location.location[1],
			},
		});

		/* Popup for marker */

		// const popup = new mapboxgl.Popup({
		// 	closeOnClick: false,
		// 	closeButton: false,
		// }).setHTML("<h4 style='color: #f76d37; margin: 0.3rem 0.5rem 0rem 0.5rem'>" + propertyData.title + "</h4>");

		/* Adding marker to mapbox */

		map.current.setCenter([location.location[1], location.location[0]]);
		map.current.setZoom(13.5);
		setMarker((prev: any) => {
			if (prev) prev.remove();
			return (
				new mapboxgl.Marker({
					color: theme.palette.primary.main,
				})
					.setLngLat([location.location[1], location.location[0]])
					// .setPopup(popup)
					.addTo(map.current)
			);
			// .togglePopup();
		});

		// map.current.setCenter([location.location[1], location.location[0]]);
		// map.current.setZoom(13.5);
		// setMarker((prev: any) => {
		// 	if (prev) prev.remove();
		// 	return new mapboxgl.Marker({
		// 		color: theme.palette.primary.main,
		// 	})
		// 		.setLngLat([location.location[1], location.location[0]])
		// 		// .setPopup(popup)
		// 		.addTo(map.current)
		// 		.togglePopup();
		// });

		/* eslint-disable-next-line react-hooks/exhaustive-deps*/
	}, [isMapboxThingyLoaded, location]);

	/*

	& let's get the location and pincode from the map and set it to the state

	*/

	const handleLocationChange = async ({
		position,
		address,
	}: {
		position: { lat: number; lng: number };
		address: string;
	}) => {
		setState({ position, address });
		setChange(position);
		setDisabledText(false);
		setHelperText("");
		await dispatch(
			SetDefaultLocationThunk({
				location: [position.lat, position.lng],
			}),
		);
	};

	/*

	& let's update the location and pincode in the database and set it to the state using thunk

	*/

	const mapcardcontainer = (typeof window !== "undefined" || undefined) && document.getElementById("map");

	const submitForm = async () => {
		if (customTitleCheck && customTitle == "") {
			setCustomTitleError(localLang.please_enter_custom_title);
			return;
		}
		setLoad(true);
		setUpdateLocationButtonDisabled(true);
		setUpdateLocationButtonText(localLang.updating);
		await dispatch(
			SetDefaultLocationThunk({
				location: [state.position.lat, state.position.lng],
			}),
		);
		await dispatch(
			updateLocationPropertyThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				property_id: property_id ? property_id : "",
				location_title: customTitleCheck
					? customTitle
					: checked
						? locaionText
						: fromList || locaionText
							? (fromList ?? locaionText)
							: address,
				pincode: value ?? "",
				location_coordinates: [change.lng, change.lat],
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context
						? profile_context.business_profile_id
						: ""
					: property_created_by
						? (property_created_by.business_profile_id as BusinessProfileInterface)._id
						: "",
				url: router.asPath ? router.asPath : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
			}),
		);
		// dispatch(
		// 	fetchNearbyPropertiesThunk({
		// 		slug: router.query ? (router.query.slug as string) : "",
		// 		sessionId: sessionId,
		// 	}),
		// );
		setUpdateLocationButtonText(localLang.update);
		setUpdateLocationButtonDisabled(false);
		setIsEditing(false);
		mapcardcontainer
			? mapcardcontainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		updateTrigger(true);
		setLoad(false);
	};

	/*

	& let's use reverseGeocodeApi to get the locality and pincode from the coordinates and set it to the state

	*/

	React.useEffect(() => {
		const executor: Function = async () => {
			const googleMapsReverseGeocodingApiResponse: GoogleMapsReverseGeocodingApiResponse = await (
				await fetch(
					"https://maps.googleapis.com/maps/api/geocode/json?latlng=" +
						change.lat +
						"," +
						change.lng +
						"&key=" +
						(process.env.GOOGLE_MAPS_API_KEY || ""),
					{ cache: "no-store" },
				)
			).json();

			const userCurrentAddresses: Array<string> | undefined = googleMapsReverseGeocodingApiResponse?.results.map(
				(result) => {
					return result.address_components?.find(
						(address_component: GMRGCResultsAddressComponent) =>
							address_component.types.includes("sublocality_level_1") ||
							address_component.types.includes("postal_town") ||
							address_component.types.includes("locality"),
					)?.long_name as string;
				},
			);

			let userCurrentAddress: string | undefined;

			let resultPos: number = 0;

			for (let i = 0; i < userCurrentAddresses.length; i++) {
				if (userCurrentAddresses[i] !== "Bengaluru") {
					if (userCurrentAddresses[i] !== undefined) {
						userCurrentAddress = userCurrentAddresses[i];
						resultPos = i;
						break;
					} else {
						i++;
					}
				}
			}

			setAddress(userCurrentAddress || "");

			const userCurrentAddressPin: string | undefined = googleMapsReverseGeocodingApiResponse?.results[
				resultPos
			]?.address_components?.find(
				(address_component: GMRGCResultsAddressComponent) =>
					address_component.types.includes("postal_code") || address_component.types.includes("pincode"),
			)?.long_name;

			setValue(userCurrentAddressPin || "");
		};
		executor();
	}, [change, setValue, dispatch, propertyData._id]);

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	const is_user_creator: boolean =
		profile_context_user !== ""
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	const [checked, setChecked] = React.useState(false);
	const [customTitleError, setCustomTitleError] = React.useState("");
	const [customTitleCheck, setCustomTitleCheck] = React.useState(false);

	const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
		setChecked(event.target.checked);
		setHelperText(localLang.pinLocation);
	};

	const handleChangeCustomTitleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCustomTitleCheck(event.target.checked);
		// setHelperText("Pin your location on the map");
	};

	return (
		<MapboxContainer id="map">
			<Snack
				open={map_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>
			<Contain>
				<Heading>{localLang.aboutLocation}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					propertyData.assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id="spp_edit_location"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									if (section_to_edit !== "property-map") {
										dispatch(setSectionToEditThunk("property-map"));
									}
									setIsEditing(true);
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : updateLocationButtonDisabled ? undefined : (
							<IconButton
								id="spp_cancel_edit_location"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									setIsEditing(false);
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>
			<TextContainer>
				{/*

				& let's check if the  edit button is clicked and if yes then show the edit view

				*/}

				{!isEditing ? null : (
					/* Edit View */

					<EditContainer>
						<SearchContainer>
							{/* {!checked ? ( */}
							<Search
								dispatch={dispatch}
								propertyData={propertyData}
								locationData={location}
								change={checked ? locaionText : address}
								fromList={fromList}
								setFromList={setFromList}
								firstLoad={firstLoad}
								setFirstLoad={setFirstLoad}
								updateLocationButtonDisabled={updateLocationButtonDisabled}
								emptyText={emptyText}
								setEmptyText={setEmptyText}
								helperText={helperText}
							/>
							{/* ) : null} */}
							{/*
							{checked ? (
								<TextField
									id="outlined-basic"
									label={helperText !== "" ? localLang.pinLocation : localLang.enterLocation}
									variant="outlined"
									value={locaionText}
									error={helperText !== ""}
									helperText={helperText}
									onChange={(e) => {
										setLocationText(e.target.value);
									}}
									disabled={helperText !== "" ? true : false}
									sx={{
										width: "100%",
										margin: "1rem 0rem 0rem 0rem",
									}}
									placeholder={helperText !== "" ? localLang.pinLocation : localLang.enterLocation}
								/>
							) : null} */}
						</SearchContainer>

						{customTitleCheck ? (
							<SearchContainer>
								<TextField
									id="spp_map_card_custom_title"
									label={localLang.enter_custom_title}
									variant="outlined"
									value={customTitle}
									error={customTitleError !== ""}
									helperText={customTitleError}
									onChange={(e) => {
										if (e.target.value) {
											setCustomTitleError("");
										} else {
											setCustomTitleError("Please enter your location's custom title");
										}
										setCustomTitle(e.target.value);
									}}
									sx={{
										width: "100%",
										margin: "1rem 0rem 0rem 0rem",
									}}
									placeholder={localLang.enter_custom_title}
								/>
							</SearchContainer>
						) : undefined}

						<FormControlLabel
							control={
								<Checkbox
									id="spp_map_card_custom_title_checkbox"
									checked={customTitleCheck}
									disabled={updateLocationButtonDisabled}
									onChange={handleChangeCustomTitleCheckbox}
								/>
							}
							label="Enter Title manually"
						/>

						{/* <FormControlLabel
							control={
								<Checkbox
									checked={checked}
									onChange={handleChangeCheckbox}
									disabled={updateLocationButtonDisabled}
								/>
							}
							label={localLang.enterManually}
						/> */}

						{isLoaded && (
							<LocationPicker
								containerElement={<MapContainer showMap={isEditing} />}
								mapElement={<Map />}
								defaultPosition={{
									lat: location.location[0],
									lng: location.location[1],
								}}
								radius={10}
								onChange={handleLocationChange}
							/>
						)}

						{/* Update Button */}

						{load ? (
							<CircularProgress
								// size={24}
								sx={{
									marginTop: "1rem",
								}}
							/>
						) : (
							<BeegruButton
								id="spp_map_card_update_location"
								sx={{ margin: "1rem 0rem 0rem 0rem", width: "100%" }}
								flavor="primary"
								onClick={() => {
									if (emptyText) {
										setHelperText(localLang.please_enter_location);
									} else {
										submitForm();
									}
								}}
								variant="contained"
								disabled={updateLocationButtonDisabled || (checked && locaionText == "") ? true : false}
								type="button"
								name="update location"
							>
								{updateLocationButtonText}
							</BeegruButton>
						)}
					</EditContainer>
				)}
			</TextContainer>
			<MapContainer2 showMap={!isEditing}>
				<Map2
					id={"mapbox_id"}
					ref={map}
					onMouseEnter={enableScrollZoom}
					onMouseLeave={disableScrollZoom}
					onTouchStart={enableScrollZoom}
					onTouchEnd={disableScrollZoom}
				>
					<InfoBox
						loading={load}
						title={propertyData.location.title}
						location={location.location}
						localLang={localLang}
					/>
				</Map2>
			</MapContainer2>

			{/* Vicinity User View */}

			{is_user_creator ||
			session?.user?.dbUser?.access_level.includes("super_admin") ||
			(session?.user?.dbUser?.access_level.includes("admin") &&
				session?.user?.dbUser?.access_level.includes("property")) ||
			propertyData?.property?.transaction_type === "ofb" ? null : (
				<VicinityContainer>
					{/* <Heading>Vicinity</Heading> */}
					<TabContextContainer value={tabValue}>
						<Box
							sx={{
								borderBottom: "none",
								borderColor: "divider",
								display: "flex",
								justifyContent: "flex-start",
								width: "100%",
							}}
						>
							<Tabs
								value={tabValue}
								onChange={handleChange}
								variant="scrollable"
								scrollButtons="auto"
								allowScrollButtonsMobile
								TabIndicatorProps={{ style: { display: "none" } }}
								sx={{
									".MuiTabs-scrollButtons.Mui-disabled": {
										display: "none",
									},
									"& button": {
										borderRadius: "12px",
										minHeight: "2rem",
										padding: "0rem 0.5rem",
									},
									minHeight: "2rem",
									margin: "0rem 0rem 0.5rem 0rem",
								}}
							>
								<Tab
									label={localLang.schools}
									value="schools"
									sx={{
										border: tabValue === "schools" ? "1px solid  #FC8019" : "none",
										color: tabValue === "schools" ? "#FC8019" : "inherit",
									}}
								/>
								<Tab
									label={localLang.malls}
									value="malls"
									sx={{
										border: tabValue === "malls" ? "1px solid  #FC8019" : "none",
										color: tabValue === "malls" ? "#FC8019" : "inherit",
									}}
								/>
								<Tab
									label={localLang.hospitals}
									value="hospitals"
									sx={{
										border: tabValue === "hospitals" ? "1px solid  #FC8019" : "none",
										color: tabValue === "hospitals" ? "#FC8019" : "inherit",
									}}
								/>
								<Tab
									label={localLang.others}
									value="others"
									sx={{
										border: tabValue === "others" ? "1px solid  #FC8019" : "none",
										color: tabValue === "others" ? "#FC8019" : "inherit",
									}}
								/>
							</Tabs>
						</Box>
						<TabPanel
							value="schools"
							sx={{ width: "100%", padding: "0rem 0rem 0rem 0.75rem" }}
						>
							{property_vicinity.map((item) => {
								if (item.category === "schools") {
									const sortedValues = [...item.values].sort((a, b) => a.distance - b.distance);
									return sortedValues.map((item) => {
										return (
											<Box
												key={item.title}
												sx={{
													display: "flex",
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
													width: "100%",
													gap: "0.75rem",
													padding: "0.25rem 0rem 0.25rem 0rem",
												}}
											>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.title}
												</Typography>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.distance + localLang.km}
												</Typography>
											</Box>
										);
									});
								}
							})}
						</TabPanel>
						<TabPanel
							value="malls"
							sx={{ width: "100%", padding: "0rem 0rem 0rem 0.75rem" }}
						>
							{property_vicinity.map((item) => {
								if (item.category === "malls") {
									const sortedValues = [...item.values].sort((a, b) => a.distance - b.distance);
									return sortedValues.map((item) => {
										return (
											<Box
												key={item.title}
												sx={{
													display: "flex",
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
													width: "100%",
													gap: "0.75rem",
													padding: "0.25rem 0rem 0.25rem 0rem",
												}}
											>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.title}
												</Typography>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.distance + localLang.km}
												</Typography>
											</Box>
										);
									});
								}
							})}
						</TabPanel>
						<TabPanel
							value="hospitals"
							sx={{ width: "100%", padding: "0rem 0rem 0rem 0.75rem" }}
						>
							{property_vicinity.map((item) => {
								if (item.category === "hospitals") {
									const sortedValues = [...item.values].sort((a, b) => a.distance - b.distance);
									return sortedValues.map((item) => {
										return (
											<Box
												key={item.title}
												sx={{
													display: "flex",
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
													width: "100%",
													gap: "0.75rem",
													padding: "0.25rem 0rem 0.25rem 0rem",
												}}
											>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.title}
												</Typography>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.distance + localLang.km}
												</Typography>
											</Box>
										);
									});
								}
							})}
						</TabPanel>
						<TabPanel
							value="others"
							sx={{ width: "100%", padding: "0rem 0rem 0rem 0.75rem" }}
						>
							{property_vicinity.map((item) => {
								if (item.category === "others") {
									const sortedValues = [...item.values].sort((a, b) => a.distance - b.distance);
									return sortedValues.map((item) => {
										return (
											<Box
												key={item.title}
												sx={{
													display: "flex",
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
													width: "100%",
													gap: "0.75rem",
													padding: "0.25rem 0rem 0.25rem 0rem",
												}}
											>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.title}
												</Typography>
												<Typography
													sx={{
														fontSize: "0.825rem",
														[theme.breakpoints.up("sm")]: {
															fontSize: "0.875rem",
														},
														[theme.breakpoints.up("md1190")]: {
															fontSize: "1rem",
														},
														fontWeight: 400,
													}}
												>
													{item.distance + localLang.km}
												</Typography>
											</Box>
										);
									});
								}
							})}
						</TabPanel>
					</TabContextContainer>
				</VicinityContainer>
			)}
		</MapboxContainer>
	);
};
export default Mapbox;
